import type { PackKind, PackTier, PackVisibility, PublishedPack } from '@planuze/admin-contracts';

/* Modelo dos filtros client-side do catálogo de packs (ADR-0704). Busca, filtragem e ordenação
   são puras sobre a lista já carregada (usePublishedPacksList) — todo campo usado aqui existe no
   `publishedPackDto`. "Mais recentes" ordena por `lastUpdated` (o DTO não expõe um createdAt
   próprio; a data live do version-index é a única temporalidade disponível). */

type VisibilityFilter = 'all' | PackVisibility;
type VerifiedFilter = 'all' | 'verified';
type ActiveFilter = 'all' | 'active';
type CatalogSort = 'installs' | 'name' | 'recent';

export interface CatalogFilters {
  readonly search: string;
  readonly visibility: VisibilityFilter;
  readonly tiers: readonly string[];
  readonly kinds: readonly string[];
  readonly verified: VerifiedFilter;
  readonly active: ActiveFilter;
  readonly sort: CatalogSort;
}

export const DEFAULT_CATALOG_FILTERS: CatalogFilters = {
  search: '',
  visibility: 'all',
  tiers: [],
  kinds: [],
  verified: 'all',
  active: 'all',
  sort: 'installs',
};

/* Os rótulos de cada opção (tier/kind/visibilidade) NÃO estão aqui — moram no bilíngue
   `content/sections/packs-catalog.ts#filterModel`, consumido só por `packs-catalog-filters.tsx`.
   Este módulo permanece dono da ORDEM (estrutural, não é texto) e da lógica pura de filtro/sort. */
export const TIER_ORDER: readonly PackTier[] = ['free', 'pro', 'team', 'enterprise'];
export const KIND_ORDER: readonly PackKind[] = ['template', 'extension', 'module-pack'];
export const VISIBILITY_FILTER_ORDER: readonly VisibilityFilter[] = [
  'all',
  'public',
  'org-private',
  'byMyself',
];

/* `true` quando algum filtro (não a ordenação) está ativo → distingue empty-state
   "sem resultados" de "sem itens" e habilita o "Limpar filtros". */
export function catalogFiltersActive(filters: CatalogFilters): boolean {
  return (
    filters.search.trim().length > 0 ||
    filters.visibility !== 'all' ||
    filters.tiers.length > 0 ||
    filters.kinds.length > 0 ||
    filters.verified !== 'all' ||
    filters.active !== 'all'
  );
}

function matchesFilters(pack: PublishedPack, filters: CatalogFilters, query: string): boolean {
  if (
    query.length > 0 &&
    !pack.displayName.toLowerCase().includes(query) &&
    !pack.id.toLowerCase().includes(query)
  ) {
    return false;
  }
  if (filters.visibility !== 'all' && pack.visibility !== filters.visibility) return false;
  if (filters.tiers.length > 0 && !filters.tiers.includes(pack.tier)) return false;
  if (filters.kinds.length > 0 && !filters.kinds.includes(pack.kind)) return false;
  if (filters.verified === 'verified' && !pack.verified) return false;
  if (filters.active === 'active' && pack.latestActive === null) return false;
  return true;
}

function sortPacks(packs: readonly PublishedPack[], sort: CatalogSort): PublishedPack[] {
  const byName = (a: PublishedPack, b: PublishedPack): number =>
    a.displayName.localeCompare(b.displayName, 'pt-BR');
  const copy = [...packs];
  if (sort === 'name') return copy.sort(byName);
  if (sort === 'recent') {
    return copy.sort(
      (a, b) =>
        new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime() || byName(a, b),
    );
  }
  return copy.sort((a, b) => b.installs - a.installs || byName(a, b));
}

/* Aplica busca + filtros + ordenação sobre a lista carregada. Chamada via useMemo no call site. */
export function applyCatalogFilters(
  packs: readonly PublishedPack[],
  filters: CatalogFilters,
): PublishedPack[] {
  const query = filters.search.trim().toLowerCase();
  const filtered = packs.filter((pack) => matchesFilters(pack, filters, query));
  return sortPacks(filtered, filters.sort);
}
